// ** React Imports
import React, { ReactNode, useEffect, Fragment } from 'react'

// ** Next Imports
import { Router, useRouter } from 'next/router'
import type { NextPage } from 'next'
import type { AppProps } from 'next/app'
import { GoogleTagManager } from '@next/third-parties/google'

// ** Loader Import
import NProgress from 'nprogress'

// ** Emotion Imports
import { CacheProvider } from '@emotion/react'
import type { EmotionCache } from '@emotion/cache'

// ** Config Imports
import themeConfig from 'src/configs/themeConfig'
import featureFlagConfig from 'src/configs/feature-flag'

// ** Third Party Import
import { YandexMetricaProvider } from 'next-yandex-metrica'
import { Toaster } from 'react-hot-toast'
import ThemeComponent from 'src/@core/theme/ThemeComponent'
import WindowWrapper from 'src/@core/components/window-wrapper'

// ** Effector Imports
import { useStore } from 'effector-react'

// ** Contexts
import globalStore from 'src/state-manager/global'
import ErrorPage from 'src/views/pages/error-page'
import { LocalizationProvider } from 'src/context/LocalizationContext'
import { HistoryProvider } from 'src/context/HistoryContext'
import { SettingsConsumer, SettingsProvider } from 'src/@core/context/settingsContext'

// ** Modules Imports
import 'dayjs/locale/ru'
import { useIntl } from 'react-intl'
import ResizeObserver from 'resize-observer-polyfill'
import { FlagProvider, useFlag } from '@unleash/proxy-client-react'

// ** Styled Components
import ReactHotToast from 'src/@core/styles/libs/react-hot-toast'

// ** Utils Imports
import { createEmotionCache } from 'src/@core/utils/create-emotion-cache'

// ** Prismjs Styles
import 'prismjs'
import 'prismjs/themes/prism-tomorrow.css'
import 'prismjs/components/prism-jsx'
import 'prismjs/components/prism-tsx'

// ** React Perfect Scrollbar Style
import 'react-perfect-scrollbar/dist/css/styles.css'

import 'src/iconify-bundle/icons-bundle-react'

// ** Source code Imports
import MetadataHeaders from 'src/layouts/MetadataHeaders'
import TechnicalBreakPage from 'src/views/pages/technical-break'
import DefaultLayout from 'src/layouts/default-layout'
import CookiesPolicy from 'src/layouts/components/cookie-policy'

// ** Global css styles
import '../../styles/globals.css'
import '../assets/style.css'

// ** Extend App Props with Emotion
type ExtendedAppProps = AppProps & {
  Component: NextPage
  emotionCache: EmotionCache
}

type GuardProps = {
  children: ReactNode
}

const clientSideEmotionCache = createEmotionCache()

// ** Pace Loader
if (themeConfig.routingLoader) {
  Router.events.on('routeChangeStart', () => {
    NProgress.start()
  })
  Router.events.on('routeChangeError', () => {
    NProgress.done()
  })
  Router.events.on('routeChangeComplete', () => {
    NProgress.done()
  })
}

const Guard = ({ children }: GuardProps) => {
  const { pathname } = useRouter()

  const isTechnicalBreak = useFlag('is_closed_for_technical_break')
  const isLandingNewsAvailable = useFlag('is_landing_news_available')

  const { formatMessage } = useIntl()

  const { language } = useStore(globalStore.locale)

  const isCloseLandingNews =
    (!isLandingNewsAvailable || language === 'en') && (pathname === '/news/[id]' || pathname === '/news')
  const isCloseCookiePolicy = language === 'en' && pathname === '/cookie-policy'
  const isClosePrivacyPolicy = language === 'en' && pathname === '/privacy-policy'

  if (isCloseLandingNews || isCloseCookiePolicy || isClosePrivacyPolicy) {
    return (
      <ErrorPage
        code={404}
        title={formatMessage({ id: 'page-not-found' })}
        description={formatMessage({ id: 'page-not-found-description' })}
      />
    )
  }

  if (isTechnicalBreak) return <TechnicalBreakPage />

  return <Fragment>{children}</Fragment>
}

// ** Configure JSS & ClassName
const App = (props: ExtendedAppProps) => {
  const { Component, emotionCache = clientSideEmotionCache, pageProps } = props

  // Variables
  const getLayout = Component.getLayout ?? (page => <DefaultLayout>{page}</DefaultLayout>)

  const setConfig = Component.setConfig ?? undefined

  useEffect(() => {
    if (window) window.ResizeObserver = ResizeObserver
  }, [])

  return (
    <CacheProvider value={emotionCache}>
      <YandexMetricaProvider
        tagID={94806853}
        initParameters={{
          clickmap: true,
          trackLinks: true,
          accurateTrackBounce: true,
          webvisor: true
        }}
      >
        <FlagProvider config={featureFlagConfig}>
          <LocalizationProvider>
            <HistoryProvider>
              <SettingsProvider {...(setConfig ? { pageSettings: setConfig() } : {})}>
                <SettingsConsumer>
                  {({ settings }) => {
                    return (
                      <ThemeComponent settings={settings}>
                        <MetadataHeaders />
                        <WindowWrapper>
                          <Guard>{getLayout(<Component {...pageProps} />)}</Guard>
                        </WindowWrapper>
                        <ReactHotToast>
                          <Toaster position={settings.toastPosition} toastOptions={{ className: 'react-hot-toast' }} />
                        </ReactHotToast>
                        <CookiesPolicy />
                      </ThemeComponent>
                    )
                  }}
                </SettingsConsumer>
              </SettingsProvider>
            </HistoryProvider>
          </LocalizationProvider>
        </FlagProvider>
        <GoogleTagManager gtmId='GTM-MT7T97JR' />
      </YandexMetricaProvider>
    </CacheProvider>
  )
}

export default App
